/**
 * tree.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */
import bluebird from "bluebird";
import moment from "moment-timezone";

import { DeviceNode } from "../models/auth";
import { MAX_UINT32 } from "../models/constants";
import { RatchetTree, UpdatePathNode } from "../models/crypto";
import { ProtocolScheme, GroupPKI, Ratchet, RatchetTreeNode } from "../models/group";
import { MessageFrame } from "../models/message";

import { cryptoServiceFactory, CryptoService } from "../services/crypto.service";
import { base64ToBuffer, bufferToBase64 } from "./encoding.service";

export const MAX_RESUMPTION_COUNT = 29;

export interface TreeService {
  addDeviceToGroup (group: RatchetTree, device: DeviceNode, parent?: RatchetTreeNode, generateKeyPair?: boolean): [RatchetTreeNode | undefined, RatchetTreeNode],
  cloneTreeNode(node: RatchetTreeNode): RatchetTreeNode,
  doTreeUpdatePath(group: RatchetTree, startIndex: number): Promise<[Array<UpdatePathNode>, Uint8Array]>,
  decryptUpdatePath(group: RatchetTree, updatePath: UpdatePathNode[],
        myIndex: number, senderIndex: number): [UpdatePathNode, Uint8Array],
  fillTreeSecrets (group: RatchetTree, updatePath: UpdatePathNode[], startIndex: number, path_secret: Uint8Array, firstPubKey: string): Promise<Uint8Array>,
  keyScheduleDerivation (group: RatchetTree, init_secret: ArrayBuffer,
        commit_secret: ArrayBuffer, forEpoch: number, psk_secret?: ArrayBuffer): Promise<ArrayBuffer>,
  computeSecretTree (rtree: RatchetTree, atIndex: number,
        tree_node_secret?: ArrayBuffer, encryption_secret?: ArrayBuffer): Promise<void>,
  computeKeyAndNonce(input_secret: ArrayBuffer, index: number, generation: number, guard?: Uint8Array):
        Promise<{key: ArrayBuffer, nonce: ArrayBuffer, guard: Uint8Array}>,
 computeKeyAndNonceFor(input_secret: ArrayBuffer, salt: ArrayBuffer,
        guard: Uint8Array | null | undefined): Promise<{key: ArrayBuffer, nonce: ArrayBuffer, guard: Uint8Array}>,
  updateMsgCounter(group: RatchetTree, msgCounterCheck: number): void,
  ratchetForward (group: RatchetTree, atIndex: number, ratchetProp: keyof Ratchet): Promise<ArrayBuffer>,
  prepareForRemoveAtIndex(group: RatchetTree, removeIndex: number): [RatchetTreeNode, number, RatchetTreeNode | undefined],
  computeTreeNodeHash(rtree: RatchetTree): Promise<Uint8Array>,
  recomputeNodeHashes(rtree: RatchetTree, starting: number): Promise<void>,
  computeKeyDerivationHash(init_secret: Uint8Array, commit_secret: Uint8Array,
        psk_secret?: Uint8Array): Promise<Uint8Array>
  makeSignatureData(frame: MessageFrame): Uint8Array
}

export class TreeServiceImpl implements TreeService {
  private cryptoService: CryptoService;

  constructor () {
    this.cryptoService = cryptoServiceFactory();
  }

  addDeviceToGroup (group: RatchetTree, device: DeviceNode,
      parent: RatchetTreeNode | undefined = undefined,
      generateKeyPair = false): [RatchetTreeNode | undefined, RatchetTreeNode] {
    const node: RatchetTreeNode = {
      userID: device.userID,
      deviceID: device._id,
      generation: 0,
      handshakeGeneration: 0
    };
    if (group.pki === GroupPKI.crystals) {
      node.publicKey = device.kyberKey;
      node.verifyKey = device.verifyKey;
    } else {
      node.publicKey = device.publicKey;
    }
    if (!group.treenodes) {
      group.treenodes = [];
    }
    if (group.treenodes.length <= 0) {
      group.treenodes.push(node);
      /* eslint-disable no-sparse-arrays */
      return [, node];
      /* eslint-enable no-sparse-arrays */
    } else {
      if (!parent) { parent = {generation: 0, handshakeGeneration: 0}; }
      if (generateKeyPair) {
        if (group.pki === GroupPKI.crystals) {
          const keyPair = this.cryptoService.generateKyberKeyPair();
          parent.publicKey = keyPair.publicKey.exportKeyToBase64();
          parent.privateKey = keyPair.privateKey.exportKeyToBase64();
          // Uncomment if signing is something an interior node needs to do
          //const signPair = this.cryptoService.generateSignKeyPair();
          //parent.signKey = signPair.privateKey.exportKeyToBase64();
          //parent.verifyKey = signPair.publicKey.exportKeyToBase64();
          keyPair.delete();
        } else {
          const keyPair = this.cryptoService.generateNewKeyPair();
          parent.publicKey = bufferToBase64(this.cryptoService.publicKeyToBuffer(keyPair.publicKey));
          parent.privateKey = bufferToBase64(this.cryptoService.privateKeyToBuffer(keyPair.privateKey));
        }
      }
      group.treenodes.push(parent);
      group.treenodes.push(node);
      return [parent, node];
    }
  }

  cloneTreeNode(node: RatchetTreeNode): RatchetTreeNode {
    const clone: RatchetTreeNode = {
      privateKey: node.privateKey,
      publicKey: node.publicKey,
      generation: node.generation,
      handshakeGeneration: node.handshakeGeneration,
      nodeHash: node.nodeHash
    };
    if (node.userID) {
      clone.userID = node.userID;
      clone.deviceID = node.deviceID;
    }
    if (node.verifyKey) {
      clone.verifyKey = node.verifyKey;
      clone.signKey = node.signKey;
    }
    return clone;
  }

  async doTreeUpdatePath(group: RatchetTree, startIndex: number): Promise<[Array<UpdatePathNode>, Uint8Array]> {
    const updatePath: Array<UpdatePathNode> = [];
    let path_secret = await this.cryptoService.generateRandSecret();
    let index = startIndex;

    do {
      const node = group.treenodes[index];
      const node_secret = await this.cryptoService.deriveSecret(path_secret, "node");

      if (group.pki === GroupPKI.crystals) {
        const keyPair = this.cryptoService.deriveKyberKeyPair(node_secret);
        node.privateKey = keyPair.privateKey.exportKeyToBase64();
        node.publicKey = keyPair.publicKey.exportKeyToBase64();
        keyPair.delete();
        if (index % 2 === 0) {
          const signPair = this.cryptoService.deriveDilithiumKeyPair(node_secret);
          node.signKey = signPair.privateKey.exportKeyToBase64();
          node.verifyKey = signPair.publicKey.exportKeyToBase64();
          signPair.delete();
        }
      } else {
        const keyPair = await this.cryptoService.deriveKeyPair(node_secret);
        node.privateKey = bufferToBase64(this.cryptoService.privateKeyToBuffer(keyPair.privateKey));
        node.publicKey = bufferToBase64(this.cryptoService.publicKeyToBuffer(keyPair.publicKey));
      }

      node.nodeHash = undefined;
      //console.log("node:", index, "New pub:", node.publicKey);
      if (index === startIndex) {
        updatePath.push({
          publicKey: node.publicKey,
          verifyKey: node.verifyKey,
          index: index,
          signingIndex: -1
        });
      } else {
        const coPathIndex = startIndex < index ?
          group.rightIndex(index) : group.leftIndex(index);

        for (const i of group.iteratePublicKeys(coPathIndex)) {
          const signingNode = group.treenodes[i];
          let encData: Uint8Array;

          if (group.pki === GroupPKI.crystals) {
            const publicKey = this.cryptoService.importKyberPubKey(signingNode.publicKey as string);
            encData = this.cryptoService.kyberEncrypt(path_secret, publicKey);
            publicKey.delete();
          } else {
            const publicKey = this.cryptoService.importPublicKey(signingNode.publicKey as string);
            encData = this.cryptoService.encapsulate(path_secret, publicKey);
          }

          updatePath.push({
            publicKey: node.publicKey,
            index: index,
            signingIndex: i,
            encPathSecret: bufferToBase64(encData)
          });

          // console.log("  index:", index, "path_secret:",
          //   bufferToBase64(path_secret), "signed by:", i);
          // console.log("    node_secret:", bufferToBase64(node_secret));
          // console.log("    pub:", signingNode.publicKey?.substring(0, 100));
          // console.log("    pri:", signingNode.privateKey?.substring(0, 100));
          // console.log("   sec:", updatePath[updatePath.length -1].encPathSecret);
        }
      }

      index = group.parentIndex(index);
      path_secret = await this.cryptoService.deriveSecret(path_secret, "path");
    } while (index >= 0);

    return [updatePath, path_secret];
  }

  decryptUpdatePath(group: RatchetTree, updatePath: UpdatePathNode[],
      myIndex: number, senderIndex: number): [UpdatePathNode, Uint8Array] {
    let path_secret: ArrayBuffer | undefined;
    let nodeOfDecrypted: UpdatePathNode | null = null;

    let ancestor = group.common_ancestor(myIndex, senderIndex);
    let passedAncestor = false;

    // console.log("decryptUpdatePath, sender:", senderIndex, "me:", myIndex,
    //   "ancestor:", ancestor);

    for (const pathNode of updatePath) {
      if (!passedAncestor && pathNode.index !== ancestor) {
        continue;
      }
      passedAncestor = true;

      if (!pathNode.encPathSecret) {
        // optimistically try the next node
        continue;
      }
      const copathIndex = senderIndex < pathNode.index ?
        group.rightIndex(pathNode.index) : group.leftIndex(pathNode.index);

      const i = pathNode.signingIndex;
      if (!group.treenodes[i].privateKey) {
        console.log("no private key found for node:", i, "coPath root:", copathIndex,
              "trying next node in updatePath");
        continue;
      }

      try {
        const signingNode = group.treenodes[i];

        // console.log("Attempting decrypt, index:", pathNode.index,
        //   "priv key by:", i, "found from copath root:", copathIndex);
        // console.log("encrypted secret:", pathNode.encPathSecret);
        // console.log("pub  key:", signingNode.publicKey?.substring(0, 100));
        // console.log("priv key:", signingNode.privateKey?.substring(0, 100));

        if (group.pki === GroupPKI.crystals) {
          const privateKey = this.cryptoService.importKyberPriKey(signingNode.privateKey as string);
          path_secret = this.cryptoService.kyberDecrypt(
            base64ToBuffer(pathNode.encPathSecret as string),
            privateKey);
          privateKey.delete();
        } else {
          const privateKey = this.cryptoService.importPrivateKey(signingNode.privateKey as string);
          path_secret = this.cryptoService.decapsulate(pathNode.encPathSecret as string,
            privateKey);
        }
      } catch (err) {
        console.error("Failed decrypting path_secret at index:", pathNode.index,
          "with key from index:", i, "trying the next node if it exists.", err);
        ancestor = group.parentIndex(ancestor);
        continue;
      }

      nodeOfDecrypted = pathNode;
      break;

    }
    if (!nodeOfDecrypted) {
      throw new Error("Couldn't resolve the update path on group: " + group._id);
    }
    return [nodeOfDecrypted, path_secret as Uint8Array]
  }

  async fillTreeSecrets (group: RatchetTree,
      updatePath: UpdatePathNode[],
      startIndex: number, path_secret: Uint8Array, firstPubKey: string): Promise<Uint8Array> {

    for (const pathNode of updatePath) {
      const node = group.treenodes[pathNode.index];
      node.publicKey = pathNode.publicKey;
      node.nodeHash = undefined;
      node.privateKey = undefined;
      if (pathNode.verifyKey) {
        node.verifyKey = pathNode.verifyKey;
      }
      // console.log("fillTreeSecrets, node:", pathNode.index, "new pub:", node.publicKey);
    }

    let index = startIndex;
    do {
      const node = group.treenodes[index];
      const node_secret = await this.cryptoService.deriveSecret(path_secret, "node");

      if (group.pki === GroupPKI.crystals) {
        const keyPair = this.cryptoService.deriveKyberKeyPair(node_secret);
        node.privateKey = keyPair.privateKey.exportKeyToBase64();
        node.publicKey = keyPair.publicKey.exportKeyToBase64();
        keyPair.delete();
        if (index % 2 === 0) {
          const signPair = this.cryptoService.deriveDilithiumKeyPair(node_secret);
          node.signKey = signPair.privateKey.exportKeyToBase64();
          node.verifyKey = signPair.publicKey.exportKeyToBase64();
          signPair.delete();
        }
      } else {
        const keyPair = await this.cryptoService.deriveKeyPair(node_secret);
        node.privateKey = bufferToBase64(this.cryptoService.privateKeyToBuffer(keyPair.privateKey));
        node.publicKey = bufferToBase64(this.cryptoService.publicKeyToBuffer(keyPair.publicKey));
      }

      //console.log("node:", index, "new Pub:", node.publicKey);
      if (index === startIndex && node.publicKey !== firstPubKey) {
        console.warn("public key doesn't match reported");
      }

      index = group.parentIndex(index);
      path_secret = await this.cryptoService.deriveSecret(path_secret, "path");
    } while (index >= 0);

    return path_secret; // this wasn't used, and is the commit_secret;
  }

  async keyScheduleDerivation (group: RatchetTree, init_secret: ArrayBuffer,
      commit_secret: ArrayBuffer, forEpoch: number,
      psk_secret: ArrayBuffer = new ArrayBuffer(0)): Promise<ArrayBuffer> {
    const joiner = await bluebird.props({
      joiner_secret: this.cryptoService.deriveKDFSecret(
          init_secret as ArrayBuffer, commit_secret, "joiner"),
      salt: this.cryptoService.deriveKDFSecret(commit_secret, psk_secret, "salt")
    });
    // console.log("deriving group keys from, init:", bufferToBase64(init_secret));
    // console.log("  commit:", bufferToBase64(commit_secret));

    const extracted_join = await this.cryptoService.deriveKDFSecret(
      joiner.joiner_secret, joiner.salt, "");
    //const extracted_join = await this.cryptoService.extract(joiner.salt, joiner.joiner_secret);
    // const welcome_secret = await deriveKDFSecret(extracted_join, salt, "welcome")
    const groupContext = group.groupContext("");

    // console.log("  join:", bufferToBase64(extracted_join));
    // console.log("  context:", bufferToBase64(groupContext));
    const epoch_secret = await this.cryptoService.deriveKDFSecret(
      extracted_join, groupContext, "epoch");

    // console.log("  epoch:", bufferToBase64(epoch_secret));

    const secrets = await bluebird.props({
      sender_data_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "sender data"),
      encryption_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "encryption"),
      exporter_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "exporter"),
      authentication_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "authentication"),
      external_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "external"),
      submission_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "submission"),
      confirmation_key: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "confirm"),
      membership_key: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "membership"),
      resumption_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "resumption"),
      //data_secret: this.cryptoService.deriveKDFSecret(
        //epoch_secret, joiner.salt, "data"),
      next_init_secret: this.cryptoService.deriveKDFSecret(
        epoch_secret, joiner.salt, "init"),
      group_key: group.protocolScheme !== ProtocolScheme.mlsSharedSecret ||
          group.group_keys && group.group_keys.length > 0 ?
        bluebird.resolve(undefined) :
        this.cryptoService.deriveKDFSecret(
          epoch_secret, joiner.salt, "group")
    });

    group.sender_data_secret = secrets.sender_data_secret;
    //group.encryption_secret = secrets.encryption_secret;
    group.exporter_secret = secrets.exporter_secret;
    group.authentication_secret = secrets.authentication_secret;
    group.confirmation_key = secrets.confirmation_key;
    group.membership_key = secrets.membership_key;
    group.next_init_secret = secrets.next_init_secret;

    group.resumption_secrets.push({
      epoch: forEpoch,
      secret: secrets.resumption_secret
    });
    if (group.resumption_secrets.length > MAX_RESUMPTION_COUNT) {
      group.resumption_secrets.shift();  // remove oldest secret
    }

    if (secrets.group_key) {
      // only create this once. Might add protocol for appending to this list later.
      if (!group.group_keys) { group.group_keys = []; }
      group.group_keys.push(secrets.group_key);
    }

    // console.log("  sender:", bufferToBase64(group.sender_data_secret));
    // console.log("  next init:", bufferToBase64(group.next_init_secret));

    if (group.pki === GroupPKI.crystals) {
      const keyPair = this.cryptoService.deriveKyberKeyPair(secrets.external_secret);
      group.externalPubKey = keyPair.publicKey.exportKeyToBase64();
      group.externalPrivKey = keyPair.privateKey.exportKeyToBase64();
      keyPair.delete();

      const subKeyPair = this.cryptoService.deriveKyberKeyPair(secrets.submission_secret);
      group.submissionPubKey = subKeyPair.publicKey.exportKeyToBase64();
      group.submissionPriKey = subKeyPair.privateKey.exportKeyToBase64();
      subKeyPair.delete();
    } else {
      const keyPair = await this.cryptoService.deriveKeyPair(secrets.external_secret);
      group.externalPubKey = bufferToBase64(this.cryptoService.publicKeyToBuffer(keyPair.publicKey));
      group.externalPrivKey = bufferToBase64(this.cryptoService.privateKeyToBuffer(keyPair.privateKey));

      const subKeyPair = this.cryptoService.deriveKeyPair(secrets.submission_secret);
      group.submissionPubKey = bufferToBase64(this.cryptoService.publicKeyToBuffer(subKeyPair.publicKey));
      group.submissionPriKey = bufferToBase64(this.cryptoService.privateKeyToBuffer(subKeyPair.privateKey));
    }

    return secrets.encryption_secret;
  }

  async computeSecretTree (rtree: RatchetTree, atIndex: number,
        tree_node_secret?: ArrayBuffer, encryption_secret?: ArrayBuffer): Promise<void> {
    if (!rtree.treenodes) { throw new Error("Missing tree nodes on Ratchet Tree"); }

    const node = rtree.treenodes[atIndex];

    if (!tree_node_secret) {
      if (!encryption_secret) {
        throw new Error("Missing encryption secret when performing key schedule update");
      }
      tree_node_secret = await this.cryptoService.deriveTreeSecret(
        encryption_secret, "tree", atIndex, 0);
    }
    node.generation = 0;
    node.handshakeGeneration = 0;

    const left = rtree.leftIndex(atIndex);
    const right = rtree.rightIndex(atIndex);
    await bluebird.all([
      this.cryptoService.deriveTreeSecret(tree_node_secret, "application", atIndex, 0)
        .then(secret => node.ratchet_secret = secret),
      this.cryptoService.deriveTreeSecret(tree_node_secret, "handshake", atIndex, 0)
        .then(secret => node.handshake_secret = secret),
      left < 0 ? bluebird.resolve() :
        this.cryptoService.deriveTreeSecret(tree_node_secret, "tree", left, 0)
        .then(secret => this.computeSecretTree(rtree, left, secret)),
      right < 0 ? bluebird.resolve() :
        this.cryptoService.deriveTreeSecret(tree_node_secret, "tree", right, 0)
         .then(secret => this.computeSecretTree(rtree, right, secret))
    ]);
  }

  computeTreeNodeHash(rtree: RatchetTree): Promise<Uint8Array> {
    return this.computeTreeNodeHashRec(rtree, rtree.rootIndex);
  }

  async computeTreeNodeHashRec(rtree: RatchetTree, index: number): Promise<Uint8Array> {
    if (index < 0 || index >= rtree.treenodes.length) {
      return bluebird.resolve(new Uint8Array(0));
    }
    // const startIndex = rtree.pki === GroupPKI.pqe ? 1900 : 90;

    const node = rtree.treenodes[index];
    if (node.nodeHash) {
      // console.log("e  index:", index,
      //   "u:", node.userID,
      //   "d:", node.deviceID,
      //   "k:", node.publicKey?.substring(startIndex),
      //   "h:", bufferToBase64(node.nodeHash));
      return node.nodeHash;
    }
    const encoder = new TextEncoder();

    const pubKeyBuf = node.publicKey ? encoder.encode(node.publicKey) : new Uint8Array(0);
    let bufLen: number = 4 + pubKeyBuf.byteLength;

    if (index % 2 === 0) {
      const userBuf = node.userID ? encoder.encode(node.userID) : new Uint8Array(0);
      const devBuf = node.deviceID ? encoder.encode(node.deviceID) : new Uint8Array(0);

      bufLen += userBuf.byteLength + devBuf.byteLength;

      const buf = new ArrayBuffer(bufLen);
      const view = new Uint8Array(buf);
      const dView = new DataView(buf);
      let offset = 0;
      dView.setUint32(offset, index, true);
      offset += 4;
      view.set(userBuf, offset);
      offset += userBuf.byteLength;
      view.set(devBuf, offset);
      offset += devBuf.byteLength;
      view.set(pubKeyBuf, offset);
      offset += pubKeyBuf.byteLength;
      const hash = new Uint8Array(await this.cryptoService.hash256(buf));
      node.nodeHash = hash;
      // console.log("  index:", index,
      //   "u:", node.userID,
      //   "d:", node.deviceID,
      //   "k:", node.publicKey?.substring(startIndex),
      //   "h:", bufferToBase64(hash));
      return hash;
    } else {

      const [leftHash, rightHash] = await bluebird.all([
        this.computeTreeNodeHashRec(rtree, rtree.leftIndex(index)),
        this.computeTreeNodeHashRec(rtree, rtree.rightIndex(index))
      ]);

      bufLen += leftHash.byteLength + rightHash.byteLength;

      const buf = new ArrayBuffer(bufLen);
      const view = new Uint8Array(buf);
      const dView = new DataView(buf);
      let offset = 0;

      dView.setUint32(offset, index, true);
      offset += 4;
      view.set(pubKeyBuf, offset);
      offset += pubKeyBuf.byteLength;
      if (leftHash.byteLength > 0) {
        view.set(new Uint8Array(leftHash), offset);
        offset += leftHash.byteLength;
      }
      if (rightHash.byteLength > 0) {
        view.set(new Uint8Array(rightHash), offset);
        offset += rightHash.byteLength;
      }
      const hash = new Uint8Array(await this.cryptoService.hash256(buf));
      node.nodeHash = hash;
      // console.log("  index:", index,
      //   "k:", node.publicKey?.substring(startIndex),
      //   "l:", bufferToBase64(leftHash),
      //   "r:", bufferToBase64(rightHash),
      //   "h:", bufferToBase64(hash));
      return hash;
    }
  }

  async computeKeyDerivationHash(init_secret: Uint8Array, commit_secret: Uint8Array,
      psk_secret: Uint8Array = new Uint8Array()): Promise<Uint8Array> {
    const bufLen = init_secret.byteLength + commit_secret.byteLength + psk_secret.byteLength;
    const buf = new ArrayBuffer(bufLen);
    const view = new Uint8Array(buf);
    let offset = 0;
    view.set(init_secret, offset);
    offset += init_secret.byteLength;
    view.set(commit_secret, offset);
    offset += commit_secret.byteLength;
    view.set(psk_secret, offset);
    offset += psk_secret.byteLength;

    const hash = new Uint8Array(await this.cryptoService.hash256(buf));
    return hash;
  }

  /// intended to move from leaf to the root, forcing recomputation
  async recomputeNodeHashes(rtree: RatchetTree, starting: number): Promise<void> {
    if (starting < 0 || starting >= rtree.treenodes.length ) { return; }
    let index = starting;
    // moving from leaf to root allows us to cache each hash on the node
    // to be used for its parent nodes (and not recomputed)
    do {
      const node = rtree.treenodes[index];
      node.nodeHash = undefined;
      await this.computeTreeNodeHashRec(rtree, index);
      index = rtree.parentIndex(index);
    } while (index > 0);
  }

  computeKeyAndNonce(input_secret: ArrayBuffer, index: number, generation: number, guard?: Uint8Array):
        Promise<{key: ArrayBuffer, nonce: ArrayBuffer, guard: Uint8Array}> {
    return this.keyAndNonceFor(guard, (info) =>
      this.cryptoService.deriveTreeSecret(input_secret, info, index, generation));
  }

  computeKeyAndNonceFor(input_secret: ArrayBuffer, salt: ArrayBuffer,
        guard: Uint8Array | null | undefined): Promise<{key: ArrayBuffer, nonce: ArrayBuffer, guard: Uint8Array}> {
    return this.keyAndNonceFor(guard, (info) =>
            this.cryptoService.deriveKDFSecret(input_secret, salt, info));
  }

  keyAndNonceFor(guard: Uint8Array | null | undefined,
      derive: (info: string) => Promise<Uint8Array>): Promise<{key: ArrayBuffer, nonce: ArrayBuffer, guard: Uint8Array}> {
    let guardview: Uint32Array;
    if (guard) {
      const dataview = new DataView(guard.buffer, guard.byteOffset, 4);
      guardview = new Uint32Array(1);
      guardview[0] = dataview.getUint32(0, true);
    } else {
      guardview = new Uint32Array(1);
      crypto.getRandomValues(guardview);
    }

    return bluebird.props({
      key: derive("key"),
      nonce: derive("nonce")
        .then(nonce_secret => {
          const view = new DataView(nonce_secret.buffer);
          let val = view.getUint32(0, true) ^ guardview[0];
          if (val < 0) {
            val += 4294967296;
          }
          view.setUint32(0, val, true);
          return view.buffer;
        }),
      guard: bluebird.resolve(new Uint8Array(guardview.buffer))
    });
  }

  updateMsgCounter(group: RatchetTree, msgCounterCheck: number): void {
    if (group.msgCounter != msgCounterCheck) {
      console.error("updateMsgCounter: expected counter:", msgCounterCheck, "was:", group.msgCounter,
        "setting:", msgCounterCheck + 1);
      group.msgCounter = msgCounterCheck + 1;
    } else {
      group.msgCounter++;
    }
  }

  async ratchetForward (group: RatchetTree, atIndex: number, ratchetProp: keyof Ratchet): Promise<ArrayBuffer> {
    const leafNode = group.treenodes[atIndex];
    let generation: number;

    if (ratchetProp === "ratchet_secret") {
      leafNode.generation++;
      generation = leafNode.generation;
    } else {
      leafNode.handshakeGeneration++;
      generation = leafNode.handshakeGeneration;
    }

    leafNode[ratchetProp] = await this.cryptoService.deriveTreeSecret(
      leafNode[ratchetProp] as ArrayBuffer, "secret", atIndex, generation);

    return leafNode[ratchetProp] as ArrayBuffer;
  }

  prepareForRemoveAtIndex(group: RatchetTree, removeIndex: number): [RatchetTreeNode, number, RatchetTreeNode | undefined] {
    // We're moving the last leaf node in the group into the interior of the
    // tree. We want to copy the former root node to a position where (1) its
    // key material will be used in building an updatePath (meaning place it
    // along the copath) and (2) the moving leaf node will use that key material
    //  to decrypt the updatePath later. Without doing so, the moving leaf
    // would have no private keys along its path to the root in order to
    // participate in the next epoch. For the moving leaf, we want to ensure
    // there will exist a private key on each node from the leaf's new position
    // to the root of the tree. As such, we temporarily replace another leaf

    //const lastIndex = group.treenodes.length - 1;
    const oldRoot = group.treenodes[group.rootIndex];
    //const lastNode = group.treenodes[lastIndex];
    let displacedNode: RatchetTreeNode | undefined;

    // We don't want to clobber any leaf nodes
    const copathIndex = group.siblingIndex(removeIndex);

    //group.treenodes[removeIndex] = lastNode;

    // console.log("RemIndex:", removeIndex, "lastIndex:", group.treenodes.length - 1,
    //    "copathIndex:", copathIndex);

    if (copathIndex >= 0 && copathIndex < group.treenodes.length) {
      displacedNode = group.treenodes[copathIndex];
      group.treenodes[copathIndex] = oldRoot;
      group.treenodes[group.rootIndex] = {
        generation: 0,
        handshakeGeneration: 0
      };
    }

    return [oldRoot, copathIndex, displacedNode];
  }

  makeSignatureData(frame: MessageFrame): Uint8Array {

    //const hexRegex = /[\dA-F]{2}/gi;
    const encoder = new TextEncoder();

    const cmdBuf = encoder.encode(frame.cmd);
    const groupBuf = encoder.encode(frame.groupID);
    const created = moment.isMoment(frame.created) ? frame.created.toDate(): frame.created;
    const time = created.getTime();
    const fromUserBuf = encoder.encode(frame.fromUserID);
    const fromDevBuf = encoder.encode(frame.fromDeviceID);
    const data = frame.data as Uint8Array;

    // 8 bytes for created, 8 bytes for epoch, 8 bytes for msgCounter
    const bufLen = cmdBuf.byteLength
      + groupBuf.byteLength
      + fromUserBuf.byteLength
      + fromDevBuf.byteLength
      + data.byteLength
      + frame.senderData.byteLength
      + 16
      ;

    //console.log("allocating buffer of length:", bufLen);
    const buf = new ArrayBuffer(bufLen);
    const view = new Uint8Array(buf);
    const dView = new DataView(buf);
    let offset = 0;
    //console.log("cmd:", frame.cmd, cmdBuf.byteLength);
    view.set(cmdBuf); // at 0
    offset += cmdBuf.byteLength;

    view.set(groupBuf, offset);
    //console.log("group:", frame.groupID, "at:", offset, groupBuf.byteLength);
    offset += groupBuf.byteLength;

    let lsbTime = time & 0xffffffff;
    if (lsbTime < 0) {
      lsbTime += MAX_UINT32;
    }
    dView.setUint32(offset, lsbTime, true); // LE bytes first
    //console.log("created:", created.getTime(), "at:", offset);
    offset += 4;
    dView.setUint32(offset, Math.floor(time / MAX_UINT32), true); // BE bytes
    offset += 4;

    view.set(fromUserBuf, offset);
    //console.log("userID:", frame.fromUserID, "at:", offset, fromUserBuf.byteLength);
    offset += fromUserBuf.byteLength;

    view.set(fromDevBuf, offset);
    //console.log("devID:", frame.fromDeviceID, fromDevBuf.byteLength);
    offset += fromDevBuf.length;

    dView.setUint32(offset, frame.epoch, true);
    //console.log("epoch", frame.epoch, "at:", offset);
    offset += 4;
    dView.setUint32(offset, frame.msgCounter, true);
    //console.log("counter:", frame.msgCounter, "at:", offset);
    offset += 4;
    view.set(data, offset);
    //console.log("data at:", offset, frame.data);
    offset += data.byteLength;
    view.set(frame.senderData, offset);
    offset += frame.senderData.byteLength;
    // console.log("sig cmd:", frame.cmd);
    // console.log("group:", frame.groupID);
    // console.log("created:", time);
    // console.log("from usr:", frame.fromUserID);
    // console.log("from dev:", frame.fromDeviceID);
    // console.log("data:", data.byteLength);
    // console.log("final offset:", offset, "bufLen:", bufLen, (bufLen - offset));
    return view;
  }
}

let service: TreeService;

export function treeServiceFactory(): TreeService {
  if (!service) { service = new TreeServiceImpl(); }
  return service;
}

export function equal (aBuf: ArrayBuffer, bBuf: ArrayBuffer): boolean {
  if (aBuf.byteLength != bBuf.byteLength) { return false; }
  const a = new Uint8Array(aBuf);
  const b = new Uint8Array(bBuf);

  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
